import { Grid } from "@mui/material";
import React, { FC, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import createCache from "@emotion/cache";
import "./digital.wallet.css";
import { prefixer } from "stylis";
import rtlPlugin from "stylis-plugin-rtl";
import {
    DigitalWalletFilterRequest,
    DigitalWalletFilterResponse,
    DigitalWalletRowResult,
} from "@/models/digital.wallet";
import digitalWalletStore from "@/stores/digital.wallet.store";
import TotalSumBalance from "@/components/digitalWallet/total.sum.balance";
import DigitalWalletListFilter from "@/components/digitalWallet/digital.wallet.list.filter";
import DigitalWalletListGrid from "@/components/digitalWallet/digital.wallet.list.grid";
import { CacheProvider } from "@emotion/react";
import authStore from "@/stores/authStore";
import popupService from "@/services/popupService";
import SearchFilterTabs from "@/components/digitalWallet/search.filter.tabs";
import { checkPermissionsWithAlert, formatDate } from "@/util";
import { PermissionName } from "@/models/apiModels";
import filtersStore, { DigitalWalletsFilterData } from "@/stores/filtersStore";
import api from "@/services/api";
import TotalCountComponent from "@/components/common/resultCountComponent";

const cacheLtr = createCache({
    key: "muiltr",
});

const cacheRtl = createCache({
    key: "muirtl",
    stylisPlugins: [prefixer, rtlPlugin],
});

const DigitalWalletListPage: FC = () => {
    const [hasNext, setHasNext] = useState<boolean>(true);
    const [digitalWallets, setDigitalWallets] = useState<DigitalWalletRowResult[]>([]);
    const [filterTabsArray, setFilterTabsArray] = useState<Array<{ id: string; name: string; value: string }>>([]);
    const [searchCounter, setSearchCounter] = useState<number>(0);
    const [collapseFilterCounter, setCollapseFilterCounter] = useState<number>(0);
    const [filterTabsCounter, setFilterTabsCounter] = useState<number>(0);
    const [isSearch, setIsSearch] = useState<boolean>(false);
    const [filter, setFilter] = useState<DigitalWalletFilterRequest>({
        keywords: "",
        walletStatus: null,
        employeeNumber: "",
        expiredDate: null,
        employeeStatus: null,
        phone: "",
        lastUseDate: null,
        orderBy: "",
        employeeName: "",
        asc: false,
        pageIndex: 0,
    });
    const { t } = useTranslation();
    const setFilterTabs = async () => {
        const filters = [
            {
                id: "keyword-input",
                name: filter.keywords,
                value: t("employee.QuickSearch"),
            },
            {
                id: "employee-name-input",
                name: filter.employeeName,
                value: t("general.SearchFilterEmployeesName"),
            },
            {
                id: "wallet-status-input",
                name: filter.walletStatus ? filter.walletStatus : "",
                value: t("general.SearchFilterWalletStatus"),
            },
            {
                id: "employee-number-input",
                name: filter.employeeNumber,
                value: t("general.SearchFilterEmployeesNumber"),
            },
            {
                id: "expired-date-input",
                name: filter.expiredDate ? formatDate(filter.expiredDate.toString()) : "",
                value: t("digitalWallet.ExpiryDate"),
            },
            {
                id: "employee-status-input",
                name:
                    filter.employeeStatus === null
                        ? ""
                        : filter.employeeStatus
                            ? t("general.Active")
                            : t("general.Inactive"),
                value: t("general.SearchFilterEmployeesStatus"),
            },
            {
                id: "phone-input",
                name: filter.phone,
                value: t("general.SearchFilterPhone"),
            },
            {
                id: "last-use-date-input",
                name: filter.lastUseDate ? formatDate(filter.lastUseDate.toString()) : "",
                value: t("digitalWallet.LastUseDate"),
            },
        ];

        const filteredTabs = filters
            .filter((filter) => filter.name !== null && filter.name !== "")
            .map((filter) => ({ id: filter.id, name: filter.value, value: filter.name }));

        setFilterTabsArray(filteredTabs);
    };

    const removeFilterTab = (tabName: string, tabId: string) => {
        const newArray = [...filterTabsArray];
        const index = newArray.findIndex((tab) => tab.name === tabName);
        newArray.splice(index, 1);
        setFilterTabsArray(newArray);

        switch (tabId) {
            case "keyword-input":
                setFilter(() => ({
                    ...filter,
                    keywords: "",
                }));
                break;
            case "employee-name-input":
                setFilter(() => ({
                    ...filter,
                    employeeName: "",
                }));
                break;
            case "wallet-status-input":
                setFilter(() => ({
                    ...filter,
                    walletStatus: null,
                }));
                break;
            case "employee-number-input":
                setFilter(() => ({
                    ...filter,
                    employeeNumber: "",
                }));
                break;
            case "expired-date-input":
                setFilter(() => ({
                    ...filter,
                    expiredDate: null,
                }));
                break;
            case "employee-status-input":
                setFilter(() => ({
                    ...filter,
                    employeeStatus: null,
                }));
                break;
            case "phone-input":
                setFilter(() => ({
                    ...filter,
                    phone: "",
                }));
                break;
            case "last-use-date-input":
                setFilter(() => ({
                    ...filter,
                    lastUseDate: null,
                }));
                break;
            default:
                break;
        }
    };

    async function getDigitalWallets(): Promise<DigitalWalletFilterResponse> {
        const filterStore = filtersStore.digitalWalletsFilterData;
        if (filterStore != null) {
            if (filterStore.filter?.pageIndex !== 0) {
                if (filterStore.filter) filterStore.filter.pageIndex = 0;
            }
            setFilter(filterStore.filter ?? new DigitalWalletFilterRequest());
            setFilterTabsArray(filterStore.filterTabs);
            const responsePromise = api.getDigitalWallets(filterStore.filter ?? new DigitalWalletFilterRequest());
            return (await responsePromise).data;
        }

        const walletFilterData = new DigitalWalletsFilterData();
        walletFilterData.filter = filter;
        walletFilterData.filterTabs = filterTabsArray;
        filtersStore.setDigitalWalletsFilterData(walletFilterData);

        const responsePromise = api.getDigitalWallets(filter);
        return (await responsePromise).data;
    }

    async function startFetching() {
        setIsSearch(() => true);
        try {
            if (!checkPermissionsWithAlert(PermissionName.ListOfWallets)) {
                return;
            }
            popupService.showLoading();
            const data = await getDigitalWallets();
            const result = data;
            if (result) {
                setHasNext(result.hasNext);
                if (filter.pageIndex === 0) {
                    setDigitalWallets(result.rows);
                    digitalWalletStore.setDigitalWalletSums(
                        result.total,
                        result.totalFilter,
                        result.currentSubsidyBalanceOfAllEmployees,
                        result.currentBalanceInActiveWallets,
                        result.unpaidSubsidies,
                        result.subsidyBalance,
                        result.subsidyUsedPercentNotification
                    );
                    setFilterTabsCounter(filterTabsCounter + 1);
                } else {
                    setDigitalWallets((old) => [...old, ...result.rows]);
                }
                setIsSearch(() => false);
            }
            popupService.close();
        } catch (err) {
            popupService.showApiError(err);
            return;
        } finally {
            setIsSearch(() => false);
        }
    }

    function searchDigitalWalletsClick() {
        setDigitalWallets([]);
        filtersStore.setDigitalWalletsFilterData(null);
        setPageIndex(0);
        setSearchCounter(searchCounter + 1);
    }

    const setPageIndex = (index?: number) => {
        setFilter((prevData) => ({
            ...prevData,
            pageIndex: index ?? filter.pageIndex + 1,
        }));
    };

    async function sortBy(sortBy: string) {
        if (filter.orderBy !== sortBy) {
            setFilter((prevData) => ({
                ...prevData,
                asc: true,
                orderBy: sortBy,
            }));
        } else {
            setFilter((prevData) => ({
                ...prevData,
                asc: !filter.asc,
                orderBy: sortBy,
            }));
        }
        setPageIndex(0);
        setDigitalWallets([]);
        filtersStore.setDigitalWalletsFilterData(null);
    }

    useEffect(() => {
        let ignore = false;
        if (!ignore) {
            startFetching();
        }
        return () => {
            ignore = true;
        };
    }, [filter.pageIndex, filter.orderBy, filter.asc, searchCounter]);

    useEffect(() => {
        let ignore = false;
        if (!ignore) {
            const fetchFilterTabs = async () => {
                await setFilterTabs();
            };
            fetchFilterTabs();
        }
        return () => {
            ignore = true;
        };
    }, [filterTabsCounter]);

    return (
        <CacheProvider value={authStore.isRtl ? cacheRtl : cacheLtr}>
            {checkPermissionsWithAlert(PermissionName.ListOfWallets) ? (
                <Grid className="digital-wallet-main-block">
                    <Grid id="digital-wallet-top-block">
                        <TotalSumBalance></TotalSumBalance>
                        {/* Digital wallet search filter */}
                        <DigitalWalletListFilter
                            searchClick={searchDigitalWalletsClick}
                            filter={filter}
                            setFilter={setFilter}
                            collapseFilterClick={() => setCollapseFilterCounter(collapseFilterCounter + 1)}
                        ></DigitalWalletListFilter>
                        {/* Digital wallet search filter */}
                        <SearchFilterTabs
                            filterTabsArray={filterTabsArray}
                            removeFilterTab={removeFilterTab}
                        ></SearchFilterTabs>
                        {/* Digital wallet search result */}
                        <TotalCountComponent
                            allCount={digitalWalletStore.totalCount}
                            currentCount={digitalWalletStore.totalFilter}
                            title={t("digitalWallet.QuantityOfWallets") as string}
                        />
                    </Grid>
                    <DigitalWalletListGrid
                        sortBy={sortBy}
                        wallets={digitalWallets}
                        isSearch={isSearch}
                        fetchMore={setPageIndex}
                        hasNext={hasNext}
                        orderBy={filter.orderBy}
                        asc={filter.asc}
                        collapseFilterCounter={collapseFilterCounter}
                    ></DigitalWalletListGrid>
                    {/* Digital wallet search result */}
                </Grid>
            ) : (
                ""
            )}
        </CacheProvider>
    );
};

export default DigitalWalletListPage;
